/*
  Cobra Magic - newsletter discount code (theme only, no app).
  Loaded by sections/newsletter-popup.liquid on every page.

  After a newsletter signup the popup shows the code (snippets/newsletter-form.liquid).
  This script:
  - copies the code (Copy button);
  - adds the code to the cart, MERGED with the codes already there:
    POST /cart/update.js {discount} replaces the whole list, so existing codes
    (for example an ad code) are always sent again. This also works on an empty
    cart, so the code is there even when a product is added by a custom button
    that goes straight to checkout;
  - also keeps it in localStorage (cm_nl_code, max 30 days) until the cart has a
    product, and checks it again on later page loads and after add-to-cart
    ('ajaxProduct:added');
  - refreshes the cart drawer / cart page with the theme event 'cart:build';
  - (v6) lets the popup signup finish without a page reload. See "Signup without reload" below.
*/
(function () {
  'use strict';

  var KEY = 'cm_nl_code';
  var MAX_AGE = 30 * 864e5; // 30 days
  var MAX_TRIES = 3;        // failed attempts before a saved code is dropped
  var running = null;       // one cart update at a time

  var TEXT = {
    saved: 'Saved. It will be added to your cart when you add a product.',
    inCart: 'Saved in your cart. It applies automatically on orders of [amount]+.',
    applied: 'Applied to your cart.',
    addMore: 'Saved in your cart. Add [amount] more to use your code.',
    notApplied: "Saved in your cart, but not applied right now (it can't be combined with some other discounts).",
    pending: 'Saved in your cart. It applies automatically once your order qualifies.',
    failed: 'Enter this code at checkout.'
  };

  function same(a, b) {
    return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
  }

  function toJson(response) {
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return response.json();
  }

  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(toJson);
  }

  function load() {
    try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }

  function forget() {
    try { localStorage.removeItem(KEY); } catch (e) {}
  }

  // Keep the code for later. The 30-day clock is not restarted for the same code.
  function remember(code, failed, cartToken) {
    var saved = load();
    if (!saved || !saved.code || !same(saved.code, code) || !(Date.now() - saved.t <= MAX_AGE)) saved = { code: code, t: Date.now(), n: 0 };
    if (failed) saved.n = (saved.n || 0) + 1;
    if (cartToken) saved.cart = cartToken; // the (empty) cart that holds the code
    if (saved.n >= MAX_TRIES) { forget(); return; }
    try { localStorage.setItem(KEY, JSON.stringify(saved)); return true; } catch (e) { return false; }
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  function findCode(cart, code) {
    return (cart.discount_codes || []).filter(function (d) { return same(d.code, code); })[0];
  }

  function readable(cart) {
    return Array.isArray(cart.discount_codes) && cart.discount_codes.every(function (d) {
      return d && typeof d.code === 'string' && d.code;
    });
  }

  // Resolves to { state: 'added', discount: { code, applicable }, cart } when the code is in the cart
  // (the cart may still be empty), or { state: 'saved' } when it is only kept in this browser.
  // atSignup: also add it to an empty cart (on other pages an empty cart just waits for a product).
  function attempt(code, atSignup) {
    var before = null;
    var changed = false;
    return getCart().then(function (cart) {
      before = cart;
      // Never send a list we could not read: it would wipe the codes already in the cart.
      if (!readable(cart)) throw new Error('discount_codes unreadable');
      if (findCode(cart, code)) return cart;
      var saved = !atSignup && load();
      // The record is gone (another tab handled it) or belongs to another cart (order placed or cart
      // expired): don't put a possibly used code into this cart.
      if (!atSignup && (!saved || (saved.cart && saved.cart !== cart.token))) { forget(); return false; }
      if (!cart.item_count && !atSignup) return null;

      var codes = cart.discount_codes.map(function (d) { return d.code; });
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // keep every code that is already in the cart and add ours
        body: JSON.stringify({ discount: codes.concat(code).join(',') })
      }).then(toJson).then(getCart).then(function (after) {
        changed = true;
        return after;
      });
    }).then(function (cart) {
      if (cart === false) return { state: 'gone' };
      if (!cart) {
        if (!remember(code, false)) throw new Error('storage unavailable');
        return { state: 'saved' };
      }
      var found = findCode(cart, code);
      if (!found) throw new Error('code not accepted');
      if (cart.item_count) forget();
      else remember(code, false, cart.token); // empty cart: check it again after the first add-to-cart
      if (changed) refreshCart();
      return { state: 'added', discount: found, cart: cart };
    }).catch(function (err) {
      // Empty cart: keep it in this browser and add it after the first add-to-cart.
      if (before && !before.item_count && remember(code, false)) return { state: 'saved' };
      remember(code, true); // try again on a later page load / add-to-cart
      throw err;
    });
  }

  function addCodeToCart(code, atSignup) {
    if (running) return running;
    running = attempt(code, atSignup);
    var done = function () { running = null; };
    running.then(done, done);
    return running;
  }

  function applySavedCode() {
    var saved = load();
    if (!saved || !saved.code) return;
    if (!(Date.now() - saved.t <= MAX_AGE)) { forget(); return; }
    addCodeToCart(saved.code).catch(function () {});
  }

  function money(cents) {
    try {
      if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  function statusText(result, box) {
    if (result.state === 'saved') return TEXT.saved;
    if (result.discount.applicable) return TEXT.applied;

    var cart = result.cart;
    var min = parseFloat(box.getAttribute('data-nl-min')) || 0;
    var shopCurrency = box.getAttribute('data-nl-currency');
    var sameCurrency = !shopCurrency || !cart.currency || cart.currency === shopCurrency;
    if (!sameCurrency || min <= 0) return TEXT.pending;

    if (!cart.item_count) return TEXT.inCart.replace('[amount]', money(Math.round(min * 100)));
    var missing = Math.round(min * 100) - (cart.items_subtotal_price || 0);
    if (missing > 0) return TEXT.addMore.replace('[amount]', money(missing));
    return TEXT.notApplied;
  }

  function setStatus(boxes, text) {
    boxes.forEach(function (box) {
      var el = box.querySelector('.nl-code__status');
      if (el) el.textContent = text;
    });
  }

  // Fallback for old browsers. iOS does not select text in a readonly field.
  function legacyCopy(input) {
    var readOnly = input.readOnly;
    var ok = false;
    try {
      input.readOnly = false;
      input.select();
      input.setSelectionRange(0, input.value.length);
      ok = document.execCommand('copy');
    } catch (e) {}
    input.readOnly = readOnly;
    return ok;
  }

  function copy(input) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(input.value).then(
        function () { return true; },
        function () { return legacyCopy(input); }
      );
    }
    return Promise.resolve(legacyCopy(input));
  }

  function initBox(box) {
    var btn = box.querySelector('.nl-code__copy');
    var input = box.querySelector('.nl-code__value');
    var live = box.querySelector('.nl-code__live');
    if (!btn || !input) return;
    var timer;

    // Enter in the code field would submit the (now empty) signup form and lose this screen.
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) e.preventDefault();
    });

    btn.addEventListener('click', function () {
      copy(input).then(function (ok) {
        if (!ok) { try { input.select(); } catch (e) {} return; } // leave it selected for a manual copy
        var copied = btn.getAttribute('data-copied-label');
        btn.textContent = copied;
        if (live) live.textContent = copied;
        clearTimeout(timer);
        timer = setTimeout(function () {
          btn.textContent = btn.getAttribute('data-copy-label');
          if (live) live.textContent = '';
        }, 2000);
      });
    });
  }

  // Code boxes of a success state: Copy button, add the code to the cart, show the status.
  // Used for the success page after a reload and for a signup without reload.
  function startBoxes(boxes) {
    boxes.forEach(initBox);
    addCodeToCart(boxes[0].getAttribute('data-nl-code'), true).then(function (result) {
      setStatus(boxes, statusText(result, boxes[0]));
    }, function () {
      setStatus(boxes, TEXT.failed);
    });
  }

  // A contact form post also marks every form on the page as "posted".
  // In that case show the theme's normal thank-you text, exactly as before.
  function isContactPost() {
    var q = window.location.search;
    return /[?&]contact_posted=true/.test(q) && !/[?&]customer_posted=true/.test(q);
  }

  // ---------------------------------------------------------------------------
  // Signup without reload (only forms that contain .nl-ajax: the popup with a code).
  //
  // Bot protection stays with Shopify. Its hCaptcha script (content_for_header) wires the form up on
  // first interaction: it saves the form's submit() method, stops the normal submit, gets a token,
  // writes it into the form (h-captcha-response, plus form_key) and then calls the saved submit().
  // We set form.submit before that happens, so that call reaches sendAjax(), which POSTs exactly the
  // same fields (token included) to the same URL with fetch() and reads the page Shopify redirects
  // to. The code is taken from that response (the success state of this form), never from this page.
  // Anything unclear falls back to the normal page submit; a robot check opens Shopify's /challenge
  // page exactly as a normal submit would.
  // ---------------------------------------------------------------------------
  var NATIVE_SUBMIT = window.HTMLFormElement && window.HTMLFormElement.prototype.submit;
  var api = window.cmNewsletter = window.cmNewsletter || {};
  if (typeof api.navigate !== 'function') api.navigate = function (url) { window.location.assign(url); };
  var leaving = false;

  // Waiting times in ms (window.cmNewsletter.timing can override them, e.g. for tests).
  function wait(name) {
    var t = api.timing || {};
    var defaults = { request: 30000, token: 20000, leave: 15000, resubmit: 300 };
    return typeof t[name] === 'number' ? t[name] : defaults[name];
  }

  function cleanText(el) {
    return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function urlParts(url) {
    var a = document.createElement('a');
    a.href = url;
    return a;
  }

  function hasParam(url, name, value) {
    return new RegExp('[?&]' + name + '=' + value + '(&|#|$)').test(String(url || ''));
  }

  function withHash(url, hash) {
    return String(url).indexOf('#') > -1 ? url : url + hash;
  }

  // application/x-www-form-urlencoded, like the browser.
  function enc(value) {
    return encodeURIComponent(value).replace(/[!'()~]/g, function (c) {
      return '%' + c.charCodeAt(0).toString(16).toUpperCase();
    }).replace(/%20/g, '+');
  }

  // The same fields a normal submit() of this form sends (submit() sends no button).
  function formBody(form) {
    var parts = [];
    function add(name, value) { parts.push(enc(name) + '=' + enc(value)); }
    if (window.FormData && typeof window.FormData.prototype.forEach === 'function') {
      new window.FormData(form).forEach(function (value, name) {
        if (typeof value === 'string') add(name, value);
      });
    } else {
      Array.prototype.forEach.call(form.elements, function (el) {
        var type = String(el.type || '').toLowerCase();
        if (!el.name || el.disabled || /^(submit|button|reset|image|file)$/.test(type)) return;
        if ((type === 'checkbox' || type === 'radio') && !el.checked) return;
        add(el.name, el.value);
      });
    }
    return parts.join('&');
  }

  function contentType(res) {
    try { return (res.headers && res.headers.get && res.headers.get('content-type')) || ''; } catch (e) { return ''; }
  }

  function parseHtml(html) {
    try { return new window.DOMParser().parseFromString(html, 'text/html'); } catch (e) { return null; }
  }

  // This form as the server rendered it in the response page.
  function findTwin(doc, form) {
    if (!doc || !form.id) return null;
    var el = doc.getElementById(form.id);
    return el && el.tagName === 'FORM' ? el : null;
  }

  // -> { kind, url, redirected, twin, message }
  // success   signed up, this form's success state is in the response
  // accepted  signed up (?customer_posted=true) but the response could not be read
  // challenge Shopify redirected to its robot check (/challenge)
  // challenge-page  a robot check served at another URL
  // error     the form came back with errors (for example an invalid email)
  // exists    no success and no error (?form_type=customer): the email is already a customer
  // throttled HTTP 429
  // rejected  other HTTP errors (400 = missing/invalid captcha token, 5xx ...)
  // unclear   anything else
  function classify(form, res, html) {
    var url = res.url || '';
    var r = { kind: 'unclear', url: url, redirected: !!res.redirected, twin: null, message: '' };
    var where = url ? urlParts(url) : null;
    if (where && where.host !== window.location.host) return r;
    if (where && /\/challenge\/?$/.test(where.pathname || '')) { r.kind = 'challenge'; return r; }
    var type = contentType(res);
    var doc = !type || /html/i.test(type) ? parseHtml(html) : null;
    if (doc && doc.querySelector('.shopify-challenge__container')) { r.kind = 'challenge-page'; return r; }
    var twin = r.twin = findTwin(doc, form);
    var success = twin && twin.querySelector('.note--success');
    if (success) { r.kind = 'success'; return r; }
    if (hasParam(url, 'customer_posted', 'true')) { r.kind = 'accepted'; return r; }
    var errors = twin && twin.querySelector('.errors');
    if (errors) { r.kind = 'error'; r.message = cleanText(errors); return r; }
    if (res.status === 429) { r.kind = 'throttled'; return r; }
    if (!res.ok) { r.kind = 'rejected'; return r; }
    if (twin && hasParam(url, 'form_type', 'customer')) { r.kind = 'exists'; return r; }
    return r;
  }

  function setBusy(form, on) {
    var btn = form.querySelector('[type="submit"]');
    if (on) {
      form.setAttribute('aria-busy', 'true');
      if (btn) btn.setAttribute('aria-disabled', 'true');
    } else {
      form.removeAttribute('aria-busy');
      if (btn) btn.removeAttribute('aria-disabled');
    }
  }

  function emailInput(form) {
    return form.querySelector('input[type="email"]');
  }

  function clearMessage(form) {
    var old = form.querySelector('.nl-ajax-msg');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var input = emailInput(form);
    if (input) {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  // Inline message above the email field (theme classes: .errors / .note), announced politely.
  function showMessage(form, text, isError) {
    var st = form.__nlAjax;
    st.mode = 'ajax';
    setBusy(form, false);
    clearMessage(form);
    var box = document.createElement('div');
    box.className = (isError ? 'errors' : 'note') + ' nl-ajax-msg';
    box.id = form.id + '-msg';
    box.textContent = text;
    var input = emailInput(form);
    var anchor = form.querySelector('.newsletter__input-group') || input;
    while (anchor && anchor.parentNode !== form) anchor = anchor.parentNode;
    form.insertBefore(box, anchor || null);
    var live = form.querySelector('.nl-ajax-live');
    if (live) {
      live.textContent = '';
      setTimeout(function () { live.textContent = text; }, 50);
    }
    if (input) {
      input.setAttribute('aria-describedby', box.id);
      if (isError) input.setAttribute('aria-invalid', 'true');
      try { input.focus(); } catch (e) {}
    }
  }

  function popupOf(el) {
    while (el && el.getAttribute) {
      if (el.getAttribute('data-section-type') === 'newsletter-popup') return el;
      el = el.parentNode;
    }
    return null;
  }

  // After a success message on page load the theme sets newsletter-<section id>=opened for 200 days,
  // so the popup does not come back. Do the same, and again after the theme's own close handler
  // (it would write the shorter "closed" period) and when the page is left.
  function rememberSignup(form) {
    var popup = popupOf(form);
    if (!popup || popup.getAttribute('data-test-mode') === 'true') return;
    var name = 'newsletter-' + popup.getAttribute('data-section-id');
    var set = function () {
      try { document.cookie = name + '=opened; path=/; max-age=' + (200 * 86400) + '; SameSite=Lax'; } catch (e) {}
    };
    set();
    if (popup.id) document.addEventListener('modalClose.' + popup.id, function () { setTimeout(set, 0); });
    window.addEventListener('pagehide', set);
  }

  // Custom pixel event (Settings > Customer events). A reload used to produce a page view with
  // ?customer_posted=true; without a reload this is the signal for analytics.
  function publishSignup(form) {
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        var p = analytics.publish('cobra_magic:newsletter_signup', { form_id: form.id, context: 'popup' });
        if (p && typeof p.then === 'function') p.then(null, function () {});
      }
    } catch (e) {}
  }

  // Wait until no cart update is running, then run fn.
  function whenIdle(fn) {
    if (running) running.then(fn, fn);
    else fn();
  }

  function showSuccess(form, twin) {
    var st = form.__nlAjax;
    st.mode = 'done';
    setBusy(form, false);
    // Same markup as after a reload: this form's success state from the server response.
    var keep = form.querySelector('.h-captcha'); // Shopify's invisible captcha widget: leave it alone
    Array.prototype.slice.call(form.childNodes).forEach(function (node) {
      if (node !== keep) form.removeChild(node);
    });
    Array.prototype.slice.call(twin.childNodes).forEach(function (node) {
      form.insertBefore(document.importNode(node, true), keep || null);
    });
    var boxes = Array.prototype.slice.call(form.querySelectorAll('[data-nl-code]'));
    if (boxes.length) whenIdle(function () { startBoxes(boxes); });
    rememberSignup(form);
    var heading = form.querySelector('.nl-code__heading') || form.querySelector('.note--success');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      try { heading.focus(); } catch (e) {}
    }
    publishSignup(form);
  }

  // Leave the page for a URL the server sent us to (GET only, nothing is posted again).
  function leave(form, url) {
    var st = form.__nlAjax;
    st.mode = 'native';
    setBusy(form, true);
    watchLeave(form);
    api.navigate(url);
  }

  // The browser's own submit, at most once per fallback.
  function sendNative(form) {
    var st = form.__nlAjax;
    if (st.nativeSent) return;
    st.nativeSent = true;
    NATIVE_SUBMIT.call(form);
  }

  // Normal page submit. sameToken: the request never got an answer, so send exactly the same fields
  // again; otherwise let Shopify's captcha run again (new token) and submit normally.
  function fallback(form, sameToken) {
    var st = form.__nlAjax;
    st.mode = 'native';
    setBusy(form, true);
    watchLeave(form);
    setTimeout(function () {
      if (sameToken) { sendNative(form); return; }
      var cap = window.Shopify && window.Shopify.captcha;
      if (!form.getAttribute('data-hcaptcha-bound') && cap && typeof cap.protect === 'function') {
        try { cap.protect(form, function () { form.submit(); }); return; } catch (e) {}
      }
      form.submit(); // Shopify's captcha wrapper -> new token -> submitHook (native) -> normal submit
    }, wait('resubmit')); // Shopify ignores a second captcha run within 250 ms
  }

  // A page change that never happened (blocked, captcha closed): give the form back.
  function watchLeave(form) {
    var st = form.__nlAjax;
    clearTimeout(st.leaveTimer);
    st.leaveTimer = setTimeout(function () {
      if (leaving) return;
      st.nativeSent = false;
      showMessage(form, st.cfg.error, true);
    }, wait('leave'));
  }

  function waitForToken(form) {
    var st = form.__nlAjax;
    clearTimeout(st.tokenTimer);
    st.tokenTimer = setTimeout(function () {
      if (!st.sending && st.mode === 'ajax') setBusy(form, false); // e.g. the hCaptcha challenge was closed
    }, wait('token'));
  }

  function handle(form, r, hash) {
    var st = form.__nlAjax;
    switch (r.kind) {
      case 'success': return showSuccess(form, r.twin);
      case 'accepted': return leave(form, withHash(r.url, hash));
      // Shopify saved the form in sessionStorage (form_key) for its challenge page: open that page like a
      // normal submit would. Without it, submit normally so Shopify shows the challenge itself.
      case 'challenge': return st.persisted ? leave(form, withHash(r.url, hash)) : fallback(form, false);
      case 'error': return showMessage(form, r.message || st.cfg.error, true);
      case 'exists': return showMessage(form, st.cfg.exists, false);
      case 'throttled': return showMessage(form, st.cfg.throttled, true);
      case 'challenge-page':
      case 'rejected': return fallback(form, false);
      default: return r.redirected && r.url ? leave(form, withHash(r.url, hash)) : fallback(form, false);
    }
  }

  function sendAjax(form) {
    var st = form.__nlAjax;
    clearTimeout(st.tokenTimer);
    st.sending = true;
    st.lastToken = token(form);
    st.persisted = !!(form.elements.form_key && form.elements.form_key.value);
    setBusy(form, true);
    var action = form.getAttribute('action') || '/contact';
    var cut = action.indexOf('#');
    var hash = cut > -1 ? action.slice(cut) : '';
    var finished = false;
    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var init = {
      method: 'POST',
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'text/html,application/xhtml+xml' },
      body: formBody(form)
    };
    if (controller) init.signal = controller.signal;

    function finish(fn) {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      st.sending = false;
      fn();
    }
    var timer = setTimeout(function () {
      finish(function () { fallback(form, true); });
      if (controller) { try { controller.abort(); } catch (e) {} }
    }, wait('request'));

    fetch(cut > -1 ? action.slice(0, cut) : action, init).then(function (res) {
      return res.text().then(function (html) {
        try { return classify(form, res, html); } catch (e) {
          return { kind: 'unclear', url: res.url || '', redirected: !!res.redirected };
        }
      });
    }).then(function (r) {
      finish(function () { handle(form, r, hash); });
    }, function () {
      finish(function () { fallback(form, true); }); // no answer
    });
  }

  function token(form) {
    var el = form.elements['h-captcha-response'];
    return (el && el.value) || '';
  }

  // hCaptcha answers are single-use. Shopify never resets its widget (the page normally reloads), so a
  // second run on this page could hand back the answer we already sent, which Shopify would reject.
  // Then reset this form's widget once and let Shopify run it again.
  function renewToken(form) {
    var st = form.__nlAjax;
    var hc = window.hcaptcha;
    var widget = form.querySelector('[data-hcaptcha-widget-id]');
    var id = widget && widget.getAttribute('data-hcaptcha-widget-id');
    if (st.renewed || !id || !hc || typeof hc.reset !== 'function') return false;
    st.renewed = true;
    try { hc.reset(id); } catch (e) { return false; }
    setTimeout(function () { form.submit(); }, wait('resubmit'));
    return true;
  }

  // form.submit() replacement. Shopify's captcha calls it once the token is in the form.
  function submitHook(form) {
    var st = form.__nlAjax;
    if (st.mode === 'done' || st.sending || (st.mode === 'native' && st.nativeSent)) return;
    if (st.lastToken && token(form) === st.lastToken) {
      if (renewToken(form)) return;
    } else {
      st.renewed = false;
    }
    if (st.mode === 'native') return sendNative(form);
    sendAjax(form);
  }

  function onSubmit(e) {
    var form = e.target;
    var st = form && form.__nlAjax;
    if (!st) return;
    // Busy (sending, leaving for a fallback) or done: ignore further clicks / Enter.
    if (st.mode !== 'ajax' || st.sending) { e.preventDefault(); return; }
    clearMessage(form);
    setBusy(form, true);
    // Shopify's captcha stopped the submit and is getting a token; it then calls form.submit().
    if (e.defaultPrevented) { waitForToken(form); return; }
    e.preventDefault();
    var cap = window.Shopify && window.Shopify.captcha;
    if (cap && typeof cap.protect === 'function' && !form.getAttribute('data-hcaptcha-bound')) {
      // Not wired up yet: Shopify's documented way to submit a form from code.
      waitForToken(form);
      try { cap.protect(form, function () { form.submit(); }); return; } catch (err) {}
    }
    sendAjax(form); // no captcha on this store
  }

  function setupAjax(form) {
    var cfg = form.querySelector('.nl-ajax');
    if (!cfg || form.__nlAjax) return false;
    // Already wired up by Shopify (it kept the browser's submit), or submit is shadowed: keep the normal submit.
    if (form.getAttribute('data-hcaptcha-bound') || typeof form.submit !== 'function') return false;
    form.__nlAjax = {
      mode: 'ajax',
      sending: false,
      cfg: {
        exists: cfg.getAttribute('data-exists') || '',
        error: cfg.getAttribute('data-error') || '',
        throttled: cfg.getAttribute('data-throttled') || ''
      }
    };
    form.submit = function () { submitHook(form); };
    return true;
  }

  function initAjax() {
    if (!NATIVE_SUBMIT || typeof window.fetch !== 'function' || !window.DOMParser || !window.addEventListener) return;
    var any = false;
    Array.prototype.slice.call(document.querySelectorAll('.nl-ajax')).forEach(function (cfg) {
      var form = cfg;
      while (form && form.tagName !== 'FORM') form = form.parentNode;
      if (form && setupAjax(form)) any = true;
    });
    if (!any) return;
    window.addEventListener('submit', onSubmit, false); // after the form's own listeners (Shopify's captcha)
    window.addEventListener('pagehide', function () { leaving = true; });
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return; // back from a fallback page change (bfcache): give the form back
      leaving = false;
      Array.prototype.slice.call(document.querySelectorAll('form')).forEach(function (form) {
        var st = form.__nlAjax;
        if (!st || st.mode === 'done') return;
        clearTimeout(st.leaveTimer);
        st.mode = 'ajax';
        st.sending = false;
        st.nativeSent = false;
        setBusy(form, false);
      });
    });
  }

  function init() {
    var boxes = Array.prototype.slice.call(document.querySelectorAll('[data-nl-code]'));

    if (boxes.length && isContactPost()) {
      boxes.forEach(function (box) {
        box.textContent = box.getAttribute('data-nl-plain') || '';
        box.removeAttribute('data-nl-code');
        box.classList.remove('nl-code');
      });
      boxes = [];
    }

    if (boxes.length) {
      startBoxes(boxes);
    } else {
      applySavedCode();
    }

    // After add-to-cart: attach a code that was saved while the cart was empty.
    document.addEventListener('ajaxProduct:added', function () {
      setTimeout(applySavedCode, 300);
    });

    try { initAjax(); } catch (e) {} // without it the form simply submits normally
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
